import { useCallback, useState } from "react";
import { getOrigin } from "src/environment";
import { getApiAuthData, getDateTimeRecord } from "src/utils";
import { Notification } from "src/utils/Notification";

import { parseErrorMessage } from "../utils";
import { useIsNewEntry } from "./useIsNewEntry";
import { useDispatch } from "../redux/hooks";
import { addNewEntryRequest } from "../redux/slices/generatorFeaturesSlice";
import { IPostRequest } from "../interfaces/Post";

const apiUrl = getOrigin() + "//api/v1/main.php";
export interface IPostProps extends IPostRequest {
  shouldRunAfterAutosave?: boolean;
}
/**
 * Custom hook to handle API POST requests.
 *
 * @returns An object containing the `post` function, a loading state, and an error state.
 */
export const usePostApi = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const isNewEntry = useIsNewEntry();
  /**
   * Sends a POST request to the API.
   *
   * @param request - The API request endpoint.
   * @param body - The request body to send with the API call.
   * @param onSuccess - (Optional) A callback function to execute on a successful response.
   * @param onError - (Optional) A callback function to execute on an error response.
   * @param shouldRunAfterAutosave - (Optional) A boolean indicating whether the request should run after autosave. Defaults to false.
   * @returns A promise that resolves to the API response or undefined.
   */
  const post = useCallback(
    async ({
      request,
      body,
      onSuccess,
      onError,
      shouldRunAfterAutosave,
    }: IPostProps) => {
      setIsLoading(true);
      let errorRes = null;
      try {
        let result: any = null;
        if (window?.api?.post) {
          result = await window.api.post(request, body);
        } else {
          if (shouldRunAfterAutosave && isNewEntry) {
            dispatch(
              addNewEntryRequest({
                request,
                body,
                onSuccess,
                onError,
              }),
            );
            setIsLoading(false);
            return;
          }
          const response = await fetch(apiUrl, {
            method: "POST",
            body: JSON.stringify({
              request,
              _app: true,
              ...getApiAuthData(),
              ...body,
              dto: getDateTimeRecord(),
            }),
          });
          if (!response.ok) {
            throw new Error(`Server Error Occured! Code:${response.status}`);
          }
          result = await response.json();
        }

        if (
          result?.logout &&
          request !== "logout" &&
          request !== "release-entry-lock"
        ) {
          localStorage.removeItem("authData");
          window.location.assign("/");
          throw new Error("error in post");
        }

        if (
          result?.error &&
          request !== "logout" &&
          request !== "release-entry-lock"
        ) {
          errorRes = result;
          throw new Error(
            typeof result.error === "string"
              ? result.error
              : "Something went wrong.",
          );
        }
        if (result?.success && result?.message && !result?.warnUser) {
          Notification("Offline", result.message, "green");
        }

        if (onSuccess) onSuccess(result);

        return result;
      } catch (error) {
        setIsError(true);
        onError?.(errorRes);
        if (!String(error).includes("You're using an outdated"))
          Notification("Error", parseErrorMessage(String(error)), "red");
      } finally {
        setIsLoading(false);
      }
    },
    [isNewEntry, dispatch],
  );

  return { post, isLoading, isError };
};
